import { ReactNode } from "react";
import { DEFAULT_FONTSIZE, replaceUnderScoreAddSpace } from "../../utils/utils";
import {
  KeyValPair,
  ReportContainer,
  checkNull,
  preLineStyle,
} from "../constant";
import { THOUGHT_CONTENT_CHECKBOX_ITEMS } from "../../pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/therapy-modality-forms/PsychodynamicTherapyForm";
import { PHYSICAL_ASSESSMENT_EXAM_SECTIONS } from "../../pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/components/form-components/InitialPsychotherapy/InitialPsychotherapyConstant";

/**
 * Labeled multi-line text for use INSIDE a `SubSection`. Renders a small
 * bordered card with a lighter header bar + content, so the parent
 * `SubSection` header and these child headers read as a clear hierarchy
 * (instead of looking like stacked peer headers of the same weight).
 */
export const NestedText = ({
  label,
  val,
}: {
  label: string;
  val?: string | null;
}) => {
  if (!val?.trim()) return null;
  return (
    <div
      style={{
        border: "1px solid #e0d5c2",
        borderRadius: 4,
        margin: "4px 0",
        overflow: "hidden",
        fontSize: DEFAULT_FONTSIZE.fontSize,
      }}
    >
      <div
        style={{
          backgroundColor: "#fffaf2",
          borderBottom: "1px solid #e0d5c2",
          padding: "4px 8px",
          fontWeight: "bold",
        }}
      >
        {label}
      </div>
      <div style={{ padding: "6px 8px" }}>
        {val.split("\n").map((line, i) => (
          <div key={i} style={preLineStyle}>
            {line || " "}
          </div>
        ))}
      </div>
    </div>
  );
};

export const splitCsv = (val?: string): string[] =>
  (val ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * Splits camelCase / PascalCase tokens into spaced words before the usual
 * underscore handling, so an option name that has no label mapping still reads
 * as text ("noDelusionsOrObsessions" → "No Delusions Or Obsessions") instead of
 * printing as one merged word.
 */
const humanize = (value: string): string =>
  replaceUnderScoreAddSpace(
    (value ?? "")
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
      .trim()
  );

/**
 * Builds a lookup keyed by the LOWERCASED option value. The MSE option lists
 * disagree on value casing across the therapy forms (`disheveled` in the
 * psychodynamic form vs `Disheveled` in the individual therapy form), and the
 * API echoes back whatever was saved, so the report has to match either one.
 */
const toLabelDict = (
  options: readonly { value: string; label: string }[]
): Record<string, string> =>
  Object.fromEntries(options.map((o) => [o.value.toLowerCase(), o.label]));

/** Resolves a stored value to its form label; falls back to readable text. */
const labelFor = (
  value: string | undefined,
  dict: Record<string, string>
): string => {
  const key = (value ?? "").trim();
  if (!key) return "";
  return dict[key.toLowerCase()] ?? humanize(key);
};

/**
 * `thoughtContent` is written by two different forms into the same key: the
 * psychodynamic form's four checkboxes (si / hi / delusions / hallucinations)
 * and the MSE modal's longer list. Both sets must be mapped or the unmapped
 * ones print as merged words.
 */
export const THOUGHT_CONTENT_LABELS: Record<string, string> = {
  ...toLabelDict(
    THOUGHT_CONTENT_CHECKBOX_ITEMS.map((it) => ({
      value: it.value,
      label: it.label,
    }))
  ),
  ...toLabelDict(
    (
      PHYSICAL_ASSESSMENT_EXAM_SECTIONS.find((s) => s.key === "thoughtContent")
        ?.items ?? []
    ).map((it) => ({ value: it.name, label: it.label }))
  ),
};

const DEFENSE_MECHANISM_LABELS: Record<string, string> = {
  repression: "Repression",
  displacement: "Displacement",
  regression: "Regression",
  splitting: "Splitting",
  denial: "Denial",
  rationalization: "Rationalization",
  sublimation: "Sublimation",
  identification: "Identification",
  projection: "Projection",
  reaction_formation: "Reaction formation",
  intellectualization: "Intellectualization",
  introjection: "Introjection",
};

export const labelList = (
  values: string[],
  dict: Record<string, string>
): string =>
  values
    .map((v) => labelFor(v, dict))
    .filter(Boolean)
    .join(", ");

export const SubSection = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div
    style={{
      border: "1px solid #c9b896",
      borderRadius: 4,
      overflow: "hidden",
      margin: "4px 0",
    }}
  >
    <ReportContainer
      label={label}
      fontSize={DEFAULT_FONTSIZE.fontSize}
      color="#f8f1e7"
      borderTop={false}
    >
      <div style={{ padding: "6px 8px" }}>{children}</div>
    </ReportContainer>
  </div>
);

interface WithData {
  data: any;
}

export const RiskBlock = ({ data }: WithData) => {
  if (!data?.riskAssessment && !data?.suicideRisk && !data?.homicideRisk)
    return null;
  return (
    <SubSection label="Risk Assessment">
      <NestedText label="Risk Assessment" val={data?.riskAssessment} />
      {data?.suicideRisk && (
        <KeyValPair k="Suicide Risk" v={data.suicideRisk} />
      )}
      {data?.homicideRisk && (
        <KeyValPair k="Homicide Risk" v={data.homicideRisk} />
      )}
    </SubSection>
  );
};

/** True only for values worth printing — `null`/`undefined`/"" are skipped. */
const hasValue = (val: unknown) =>
  val !== undefined && val !== null && val !== "";

export const BillingBlock = ({ data }: WithData) => {
  const hasTimeSpend = hasValue(data?.timeSpend);
  const hasDocumentationTime = hasValue(data?.documentationTime);
  const hasBillable = hasValue(data?.billable);
  if (!hasTimeSpend && !hasDocumentationTime && !hasBillable) return null;
  return (
    <SubSection label="Billing Information">
      {hasTimeSpend && (
        <KeyValPair k="Time Spend" v={`${data?.timeSpend} minutes`} />
      )}
      {hasDocumentationTime && (
        <KeyValPair
          k="Documentation Time"
          v={`${data?.documentationTime} minutes`}
        />
      )}
      {hasBillable && (
        <KeyValPair k="Billable" v={data?.billable ? "Yes" : "No"} />
      )}
    </SubSection>
  );
};

/**
 * Psychodynamic therapy shared content (MSE + free-assoc + transference +
 * defense mechanisms + unconscious material + interpretations + insights +
 * impression + risk + billing). Used by both PSYCHOTHERAPY_NOTE
 * (modality=Psychodynamic_Therapy) and the standalone PSYCHODYNAMIC_THERAPY_NOTE.
 *
 * @param withExtras adds Mood Stated / Affect Range fields to the MSE.
 * @param primaryModalityLabel when provided, renders primaryModality as a
 *        labelled row inside the MSE (used by the standalone form, which
 *        stores appearance-like values in `primaryModality`).
 */
// withExtras / primaryModalityLabel are still accepted (callers pass them) but
// are only consumed by the commented-out MSE block below.
export const PsychodynamicBlock = ({
  data,
}: WithData & { withExtras?: boolean; primaryModalityLabel?: string }) => {
  const defenses = splitCsv(data?.defenseMechanismObserved);
  return (
    <>
      {(data?.materialBroughtToSession || data?.sessionFocus) && (
        <SubSection label="Presenting Material & Session Focus">
          <NestedText
            label="Material Brought to Session"
            val={data?.materialBroughtToSession}
          />
          <NestedText label="Session Focus/Themes" val={data?.sessionFocus} />
        </SubSection>
      )}

      {/* MSE removed per BA: it duplicated the dedicated Mental Status
          Examination (MSE) section rendered under Objective.
      <MseBlock
        data={data}
        withExtras={withExtras}
        primaryModalityLabel={primaryModalityLabel}
      />
      */}

      {(data?.freeAssociations ||
        data?.dreamContent ||
        data?.dreamAnalysis) && (
        <SubSection label="Free Association & Dream Work">
          <NestedText label="Free Associations" val={data?.freeAssociations} />
          <NestedText label="Dream Content" val={data?.dreamContent} />
          <NestedText
            label="Dream Analysis/Interpretation"
            val={data?.dreamAnalysis}
          />
        </SubSection>
      )}

      {(data?.transferenceObservations ||
        data?.counterTransferenceReflections) && (
        <SubSection label="Transference & Counter Transference">
          <NestedText
            label="Transference Observations"
            val={data?.transferenceObservations}
          />
          <NestedText
            label="Counter Transference Reflections"
            val={data?.counterTransferenceReflections}
          />
        </SubSection>
      )}

      {checkNull(defenses) && (
        <SubSection label="Resistance & Defense Mechanisms">
          <KeyValPair
            k="Defense Mechanisms Observed"
            v={labelList(defenses, DEFENSE_MECHANISM_LABELS)}
          />
        </SubSection>
      )}

      {(data?.unconsciousConflictsExplored ||
        data?.earlyChildhoodExperiences ||
        data?.relationalPatterns) && (
        <SubSection label="Unconscious Material & Patterns">
          <NestedText
            label="Unconscious Conflicts Explored"
            val={data?.unconsciousConflictsExplored}
          />
          <NestedText
            label="Early Childhood Experiences/Object Relations"
            val={data?.earlyChildhoodExperiences}
          />
          <NestedText
            label="Relational Patterns"
            val={data?.relationalPatterns}
          />
        </SubSection>
      )}

      {(data?.interpretationsOffered ||
        data?.clientResponseToInterpretations ||
        data?.walkingThroughProcess) && (
        <SubSection label="Interpretations & Working Through">
          <NestedText
            label="Interpretations Offered"
            val={data?.interpretationsOffered}
          />
          <NestedText
            label="Client Response to Interpretations"
            val={data?.clientResponseToInterpretations}
          />
          <NestedText
            label="Working Through Process"
            val={data?.walkingThroughProcess}
          />
        </SubSection>
      )}

      {(data?.insightsDeveloped ||
        data?.therapeuticRelationship ||
        data?.progressTowardTreatmentGoal) && (
        <SubSection label="Insights & Therapeutic Process">
          <NestedText
            label="Insights Developed"
            val={data?.insightsDeveloped}
          />
          <NestedText
            label="Therapeutic Relationship/Alliance"
            val={data?.therapeuticRelationship}
          />
          <NestedText
            label="Progress Toward Treatment Goals"
            val={data?.progressTowardTreatmentGoal}
          />
        </SubSection>
      )}

      {data?.clinicalImpression && (
        <SubSection label="Clinical Impression & Plan">
          <NestedText
            label="Clinical Impression"
            val={data?.clinicalImpression}
          />
        </SubSection>
      )}

      {data?.planForNextSession && (
        <SubSection label="Plan for Next Session">
          <NestedText
            label="Plan for Next Session"
            val={data?.planForNextSession}
          />
        </SubSection>
      )}

      <RiskBlock data={data} />
      <BillingBlock data={data} />
    </>
  );
};
